import * as AWS from 'aws-sdk/global';
import S3 from 'aws-sdk/clients/s3';
import { gql, Resolver } from '@apollo/client';

/**
 * @typedef {ListObjectsInS3Args}
 * @property {string} bucketName Name of the target S3 bucket
 * @property {string} bucketRegion AWS region where the bucket is hosted
 * @property {string} identityPoolId AWS Cognito identity pool ID
 * @property {boolean} listAll whether to use loop over listObjectsV2 until all pages of results are
 * fetched
 * @property {number} maxKeys the maximum number of keys to fetch in a single call to listObjectsV2.
 * The highest allowed value is 1000.
 * @property {number} maxResults the maximum number of keys to fetch overall, which can require
 * multiple calls to listObjectsV2.
 * @property {string} prefix
 * @property {string} startAfter A key after which to start listing objects. S3 lists objects in
 * UTF-8 character encoding in lexicographical order.
 */
interface ListObjectsInS3Args {
    bucketName: string;
    bucketRegion: string;
    identityPoolId: string;
    listAll?: boolean;
    maxKeys?: number;
    maxResults: number;
    prefix?: string;
    startAfter?: string;
}

interface ListObjectsInS3Params {
    Bucket: string;
    StartAfter?: string;
    ContinuationToken?: string;
    MaxKeys: number;
    Prefix?: string;
}

const throttleMs = 200;

/**
 * Lists the object keys in an S3 bucket, using the method listObjectsV2 in the AWS S3 JavaScript
 * SDK: https://docs.aws.amazon.com/AWSJavaScriptSDK/latest/AWS/S3.html#listObjectsV2-property
 * @param obj
 * @param {ListObjectInS3Args} args
 * @returns {Promise<S3ObjectsListResult>}
 */
export const listObjectsInS3: Resolver = async (
    obj: Record<string, unknown>,
    args: ListObjectsInS3Args,
): Promise<S3ObjectsListResult> => {
    const {
        bucketName,
        bucketRegion,
        identityPoolId,
        startAfter,
        maxResults,
        maxKeys = 1000,
        prefix,
        listAll,
    } = args;

    if (maxKeys > 1000) {
        return Promise.reject(new Error('maxKeys must be <= 1000'));
    }

    const credentials = new AWS.CognitoIdentityCredentials({
        IdentityPoolId: identityPoolId,
    });

    AWS.config.update({
        region: bucketRegion,
        credentials,
        retryDelayOptions: {
            base: 1000,
        },
    });

    const s3 = new S3({
        params: { Bucket: bucketName },
        region: bucketRegion,
        credentials,
    });

    const params: ListObjectsInS3Params = {
        Bucket: bucketName,
        StartAfter: startAfter,
        ContinuationToken: undefined,
        MaxKeys: listAll ? maxKeys : Math.min(maxResults, maxKeys),
        Prefix: prefix,
    };

    const objectKeys: string[] = [];
    let isTruncated = true;

    /**
     * If many ingestions are happening concurrently, we can run into a TooManyRequestsError when
     * calling methods on the S3 SDK like s3.listObjectsV2 because the S3 SDK is fetching data from
     * Cognito under the hood and it can exceed quotas on the Cognito service. To reduce the chances
     * of this, we only fire a listObjectsV2 at the rate set by throttleMs. If there is still a
     * TooManyRequestsError, the retryDelayOptions should help by setting the retry interval.
     */
    const waitAndListObjects = (): Promise<void> =>
        new Promise((resolve, reject) => {
            setTimeout((): void => {
                s3.listObjectsV2(
                    params,
                    (
                        error: AWS.AWSError,
                        data: S3.ListObjectsV2Output | null,
                    ) => {
                        if (error) {
                            reject(error);
                        }

                        if (data === null) {
                            reject(new Error('Data is null'));
                        }

                        if (!error && data !== null) {
                            for (const item of data.Contents ?? []) {
                                if (
                                    (objectKeys.length < maxResults ||
                                        listAll) &&
                                    typeof item?.Key === 'string'
                                ) {
                                    objectKeys.push(item.Key);
                                }
                            }

                            isTruncated = data.IsTruncated ?? false;

                            if (isTruncated) {
                                params.ContinuationToken =
                                    data.NextContinuationToken;
                            }

                            resolve();
                        }
                    },
                );
            }, throttleMs);
        });

    while (isTruncated && (objectKeys.length < maxResults || listAll)) {
        try {
            // eslint-disable-next-line no-await-in-loop
            await waitAndListObjects();
        } catch (error) {
            return Promise.reject(error);
        }
    }

    return Promise.resolve({ objectKeys });
};

export const LIST_OBJECTS_IN_S3 = gql`
    query ListObjectsInS3(
        $bucketName: String
        $bucketRegion: String
        $identityPoolId: String
        $startAfter: String
        $maxResults: Number
        $maxKeys: Number
        $prefix: String
        $listAll: Boolean
    ) {
        listObjectsInS3(
            bucketName: $bucketName
            bucketRegion: $bucketRegion
            identityPoolId: $identityPoolId
            startAfter: $startAfter
            maxResults: $maxResults
            maxKeys: $maxKeys
            prefix: $prefix
            listAll: $listAll
        ) @client
    }
`;
